// ui/services/trueTodoKpiSummaryService.ts
// ✅ Fix TS2339 errors:
// - UserRealtimeSummary 使用 userKey（不是 key）
// - SummaryCounts 沒有 total（此檔自行計算 total = contracts + seals + others）
//
// 本服務負責：
// 1) 用「和儀錶板同一條管線」(getTrueTodosOnce + computeTrueDashboardSummary) 重建 KPI
// 2) 寫回 dashboard_daily_summary/trueTodoLive 供 Debug 分頁與儀錶板共用

import { db, auth, functions, serverTimestamp } from "../../firebaseClient";
import { getTrueTodosOnce } from "./trueTodoAggregator";
import { computeTrueDashboardSummary } from "./todosSummary";

export type TrueTodoKpiUserKey = "kevin" | "ops" | "gm";

export type TrueTodoKpiCounts = {
  contracts: number;
  seals: number;
  others: number;
  total: number;
};

export type TrueTodoKpiSummary = {
  version: number;
  updatedAt: any; // Firestore Timestamp
  users: Record<TrueTodoKpiUserKey, TrueTodoKpiCounts>;
  totals: TrueTodoKpiCounts;
  meta: {
    mode: "server" | "client";
    rebuiltAtClient?: string;
    rebuiltBy?: string;
    excludedTodos?: number;
    processedTodos?: number;
    notes?: string;
  };
};

const SUMMARY_REF = db.collection("dashboard_daily_summary").doc("trueTodoLive");

function emptyCounts(): TrueTodoKpiCounts {
  return { contracts: 0, seals: 0, others: 0, total: 0 };
}

function n(v: any): number {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
}

function sum3(a: number, b: number, c: number) {
  return n(a) + n(b) + n(c);
}

function toLegacyCounts(counts: any): { contracts: number; seals: number; others: number } {
  // ✅ 把新版 8 模組折疊回舊版 KPI 欄位（contracts/seals/others）
  const contractReviews = n(counts?.contractReviews);
  const templates = n(counts?.templates);
  const seals = n(counts?.seals);
  const cases = n(counts?.cases);
  const govCorrespondence = n(counts?.govCorrespondence);
  const regIntel = n(counts?.regIntel);
  const projectControl = n(counts?.projectControl);
  const governanceWorkspace = n(counts?.governanceWorkspace);

  const contracts = contractReviews + templates;
  const others = cases + govCorrespondence + regIntel + projectControl + governanceWorkspace;
  return { contracts, seals, others };
}

/**
 * ✅ Client rebuild（不依賴 Cloud Functions）
 * 與儀錶板一致：trueTodoAggregator → todosSummary.computeTrueDashboardSummary
 */
export async function rebuildTrueTodoKpiSummaryClientNow(): Promise<{ ok: boolean; summary?: TrueTodoKpiSummary; error?: any }> {
  try {
    const me = auth.currentUser?.email || "unknown";

    const { trueTodos, excluded } = await getTrueTodosOnce();

    // 與儀錶板一致：同一條摘要計算
    const daily = computeTrueDashboardSummary(trueTodos);

    const byKey: Record<TrueTodoKpiUserKey, TrueTodoKpiCounts> = {
      kevin: emptyCounts(),
      ops: emptyCounts(),
      gm: emptyCounts(),
    };

    for (const u of daily.users || []) {
      // ✅ UserRealtimeSummary 的欄位是 userKey（不是 key）
      const k = (u as any)?.userKey as TrueTodoKpiUserKey;
      if (k !== "kevin" && k !== "ops" && k !== "gm") continue;

      const legacy = toLegacyCounts((u as any)?.counts);
      const contracts = legacy.contracts;
      const seals = legacy.seals;
      const others = legacy.others;

      // ✅ SummaryCounts 沒有 total → 這裡自行計算
      const total = sum3(contracts, seals, others);

      byKey[k] = { contracts, seals, others, total };
    }

    const totals: TrueTodoKpiCounts = {
      contracts: sum3(byKey.kevin.contracts, byKey.ops.contracts, byKey.gm.contracts),
      seals: sum3(byKey.kevin.seals, byKey.ops.seals, byKey.gm.seals),
      others: sum3(byKey.kevin.others, byKey.ops.others, byKey.gm.others),
      total: sum3(byKey.kevin.total, byKey.ops.total, byKey.gm.total),
    };

    const summary: TrueTodoKpiSummary = {
      version: 1,
      updatedAt: serverTimestamp(),
      users: byKey,
      totals,
      meta: {
        mode: "client",
        rebuiltAtClient: new Date().toISOString(),
        rebuiltBy: me,
        excludedTodos: n((excluded as any)?.length),
        processedTodos: totals.total,
        notes: `Rebuilt by client using dashboard pipeline (computeTrueDashboardSummary). trueTodos=${n((trueTodos as any)?.length)}.`,
      },
    };

    await SUMMARY_REF.set(summary as any, { merge: false });

    return { ok: true, summary };
  } catch (error: any) {
    console.error("[trueTodoKpiSummaryService] client rebuild failed:", error);
    return { ok: false, error };
  }
}

/**
 * 先嘗試呼叫雲端 function；若 function 不穩/沒部署 → fallback client rebuild
 */
export async function triggerRebuildTrueTodoKpiSummaryNow(): Promise<{ ok: boolean; mode: "server" | "client"; error?: any }> {
  try {
    const fn = (functions as any).httpsCallable("rebuildTrueTodoKpiSummaryNow");
    await fn({});
    return { ok: true, mode: "server" };
  } catch (e: any) {
    const r = await rebuildTrueTodoKpiSummaryClientNow();
    return { ok: r.ok, mode: "client", error: r.error };
  }
}

export async function getTrueTodoKpiSummaryOnce(): Promise<TrueTodoKpiSummary | null> {
  const snap = await SUMMARY_REF.get();
  if (!snap.exists) return null;
  return snap.data() as any;
}

export function subscribeTrueTodoKpiSummary(cb: (summary: TrueTodoKpiSummary | null) => void) {
  return SUMMARY_REF.onSnapshot(
    (snap: any) => cb(snap.exists ? (snap.data() as any) : null),
    (err: any) => {
      console.error("[trueTodoKpiSummaryService] subscribe error:", err);
      cb(null);
    }
  );
}
